import { Controller } from "@hotwired/stimulus"
import { FormHelper } from "~/helpers/form_helper"

export default class extends Controller {
  static get targets() {
    return [
      "form", "billingCycle", "level", "invoiceId", "paymentIntentId",
      "billingInformationComplete", "currentCard", "newCard", "company",
      "address", "postalCode", "city", "countryCode", "fullName", "emailAccountsModal",
      "taxId", "additionalInformation", "reviewOrder", "payButton", "emailAccounts",
      "overageQuotaPrices", "allowOverageRequests", "searchBucket", "verifyBucket",
      "cardField"
    ]
  }

  static get values() {
    return {
      stripeKey: String, baseLevel: Number, baseBillingCycle: String, baseEmailAccounts: Number,
      searches: Number, verifications: Number
    }
  }

  connect() {
    // Store reference and broadcast availability
    this.element.checkoutController = this
    window.dispatchEvent(new CustomEvent('checkoutControllerConnected', {
      detail: { controller: this }
    }))

    const updateEmailAccountsParam = new URLSearchParams(location.search).get("update_email_accounts")
    if (updateEmailAccountsParam && this.hasEmailAccountsModalTarget) {
      new bootstrap.Modal(this.emailAccountsModalTarget).show()
    }

    this.checkButtonState()
  }

  disconnect() {
    if (this.element.checkoutController === this) {
      delete this.element.checkoutController
    }
  }

  initialize() {
    var _this = this

    if (typeof App !== 'undefined' && App.whenAvailable) {
      App.whenAvailable("Stripe", function () {
        _this.initializeStripe()
        _this.loadStripeElements()
      })
    } else if (typeof Stripe !== 'undefined') {
      this.initializeStripe()
      this.loadStripeElements()
    }

    this._registerCountryCodeListener()
    this._registerReviewOrderLoadedListener()
  }

  initializeStripe() {
    this.stripe = Stripe(this.stripeKeyValue)
  }

  loadStripeElements() {
    if (!this.stripe) return
    if (!this.hasCardFieldTarget) return

    this.card = this.stripe.elements().create("card", FormHelper.elementStyles("13px"))
    this.card.mount(this.cardFieldTarget)

    // Enable button when card is ready
    this.card.on('ready', () => {
      this.checkButtonState()
    })
  }

  // New method for modal integration - show new card form
  showNewCardForm() {
    if (this.hasCurrentCardTarget) {
      $(this.currentCardTarget).hide()
    }

    if (this.hasNewCardTarget) {
      $(this.newCardTarget).show()
    }

    this.checkButtonState()
  }

  // New method for modal integration - check button state
  checkButtonState() {
    if (!this.hasPayButtonTarget) return

    const hasExistingCard = this.hasCurrentCardTarget &&
      $(this.currentCardTarget).is(':visible')

    // Enable immediately if there's an existing card
    if (hasExistingCard) {
      this.payButtonTarget.disabled = false
      return
    }

    // For other cases, use existing logic or enable as fallback
    if (this.enablePayButtonIfNeeded) {
      this.enablePayButtonIfNeeded()
    } else {
      this.payButtonTarget.disabled = false
    }
  }

  // Proceed to checkout, with 3 cases:
  //   - if the billing information aren't complete, we first save them, then the
  //     source and finally pay with it.
  //   - if user added a new source, we first save it and then pay with it.
  //   - if user uses an existing card, we immediately pay with it.
  submit(e) {
    if (typeof e != "undefined") {
      FormHelper.disableForm(e.target)
    }

    if (this.billingInformationCompleteTarget.value == "false") {
      this.saveBillingInformationAndProceedToCheckout()
    } else if ($(this.newCardTarget).is(":visible")) {
      this.createSourceOnStripeAndProceedToCheckout()
    } else {
      this.checkout()
    }
  }

  checkout() {
    var _this = this
    let level = this.levelTarget.value

    // A POST is used when user is switching subscriptions.
    // A PUT is used when the current active subscription is updated.
    // Data platform (level 5) is a special case, it uses a POST since each subscription is distinct.
    let method = "post"
    if (typeof this.formTarget.elements["_method"] != "undefined" && level != 5) {
      method = this.formTarget.elements["_method"].value
    }

    let params = {
      _method: method,
      level: level,
      billing_cycle: this.billingCycleTarget.value,
      payment_intent_id: this.paymentIntentIdTarget.value,
      invoice_id: this.invoiceIdTarget.value,
      email_accounts: (this.hasEmailAccountsTarget) ? this.emailAccountsTarget.value : null,
      allow_overage_requests: this.hasAllowOverageRequestsTarget ? this.allowOverageRequestsTarget.checked : null
    }

    // Below if for the data platform (level 5)
    if (Number(this.levelTarget.value) == 5) {
      params.searches = this.searchesValue
      params.verifications = this.verificationsValue
    }

    if (this._hasToSelectBucketToTransfer()) {
      params.bucket_to_transfer = this.searchBucketTarget.checked ? "search" : "verify"
    }

    $.ajax({
      url: this.formTarget.getAttribute("action"),
      type: "POST",
      dataType: "json",
      data: params,
      headers: { "X-CSRF-Token": $("meta[name='csrf-token']").attr("content") },
      success: function (result) {
        // If the subscription update succeeded, redirect the user to the welcome page.
        if (result.success === true) {
          return Turbo.visit(result.destination, { frame: result.frame })
        }

        // If the subscription update requires an user action, we handle
        // it through Stripe using the returned payment intent secret.
        // This matches the flow explained on
        // https://stripe.com/docs/billing/subscriptions/payment#requires-action-2
        if (result.payment_intent_secret) {
          return _this.attempt3DSChallenge(result.payment_intent_secret)
        }

        // If the subscription update failed with an invoice id, we ask the
        // user to provide a new payment method and retry. The idea is we'll
        // reattempt the same payment with another payment method. This will
        // prevent creating a duplicate on the Stripe side. This matches the flow
        // explained on https://stripe.com/docs/billing/subscriptions/payment#failure-3
        if (result.invoice_id) {
          _this.invoiceIdTarget.value = result.invoice_id

          if (_this.hasCurrentCardTarget) {
            $(_this.currentCardTarget).hide()
          }

          $(_this.newCardTarget).show()
        }

        FormHelper.displayFlashError(result.error_message)
        FormHelper.enableForm()
      },
      error: function () {
        // Something unexpected happened, we let the user know and let him retry.
        FormHelper.displayFlashError(i18n.t("js.back.checkout.errors.payment_process"))
        FormHelper.enableForm()
      }
    })
  }

  attempt3DSChallenge(intentSecret) {
    var _this = this

    this.stripe.handleCardPayment(intentSecret).then(function (result) {
      if (result.error) {
        // The user failed the 3DS challenge, we let him know through the
        // error message returned by Stripe.
        FormHelper.displayFlashError(result.error.message)
        return FormHelper.enableForm()
      }

      // The user succeeded in the 3DS challenge, provide the payment intent
      // ID to the checkout.
      _this.paymentIntentIdTarget.value = result.paymentIntent.id
      _this.checkout()
    })
  }

  saveBillingInformationAndProceedToCheckout() {
    var _this = this

    if (this.companyTarget.value != "" &&
      this.addressTarget.value != "" &&
      this.postalCodeTarget.value != "" &&
      this.cityTarget.value != "" &&
      this.countryCodeTarget.value != "") {

      App.billing_information.set("company", this.companyTarget.value)
      App.billing_information.set("address", this.addressTarget.value)
      App.billing_information.set("postal_code", this.postalCodeTarget.value)
      App.billing_information.set("city", this.cityTarget.value)
      App.billing_information.set("country_code", this.countryCodeTarget.value)
      App.billing_information.set("additional_information", this.additionalInformationTarget.value)

      if (this.countryCodeTarget.value == "IN" && this.taxIdTarget.value != "") {
        App.billing_information.set("tax_id", this.taxIdTarget.value)
      }

      App.billing_information.save(null, {
        success: function () {
          if (_this.hasNewCardTarget && $(_this.newCardTarget).is(":visible")) {
            _this.createSourceOnStripeAndProceedToCheckout()
          } else {
            // User has existing card, proceed directly to checkout
            _this.checkout()
          }
        },
        error: function () {
          FormHelper.displayFlashError(i18n.t("js.back.checkout.errors.saving_billing_infos"))
          FormHelper.enableForm()
        }
      })

      return
    }

    FormHelper.displayFlashError(i18n.t("js.back.checkout.errors.missing_billing_information"))
    FormHelper.enableForm()
  }

  createSourceOnStripeAndProceedToCheckout() {
    var _this = this

    var billingDetails = {
      name: this.fullNameTarget.value,
      address: {
        line1: (this.hasAddressTarget) ? this.addressTarget.value : gon.billing_information_address,
        city: (this.hasCityTarget) ? this.cityTarget.value : gon.billing_information_city,
        postal_code: (this.hasPostalCodeTarget) ? this.postalCodeTarget.value : gon.billing_information_postal_code,
        country: (this.hasCountryCodeTarget) ? this.countryCodeTarget.value : gon.billing_information_country_code
      },
      email: gon.user.email
    }

    this.stripe.createPaymentMethod({ type: "card", card: this.card, billing_details: billingDetails }).then(function (result) {
      if (result.error) {
        // The payment method creation on the Stripe side failed.
        FormHelper.displayFlashError(result.error.message)
        return FormHelper.enableForm()
      }

      // The payment method creation on the Stripe side succeeded, it's time to save it on our side as well.
      _this.savePaymentMethodAndProceedToCheckout(result.paymentMethod.id)
    })
  }

  savePaymentMethodAndProceedToCheckout(paymentMethodId) {
    var _this = this

    $.ajax({
      url: "/credit-cards",
      type: "POST",
      dataType: "json",
      data: { payment_method_id: paymentMethodId },
      headers: { "X-CSRF-Token": $("meta[name='csrf-token']").attr("content") },
      success: function () {
        // The payment method creation on our side succeeded, the user can be charged.
        _this.checkout()
      },
      error: function (jqXHR, _textStatus, _errorThrown) {
        void _textStatus;
        void _errorThrown;

        if (jqXHR.responseJSON) {
          FormHelper.displayFlashError(jqXHR.responseJSON.error)
        } else {
          FormHelper.displayFlashError(i18n.t("js.back.checkout.errors.saving_credit_card"))
        }

        FormHelper.enableForm()
      }
    })
  }

  updateReviewOrder() {
    // Skip if review order is not present (e.g., in modal)
    if (!this.hasReviewOrderTarget) return

    this.payButtonTarget.setAttribute("disabled", "true")

    var reviewOrderTargetURL = new URL(this.reviewOrderTarget.src, window.location.origin)

    if (this.hasCountryCodeTarget) {
      reviewOrderTargetURL.searchParams.set("country_code", this.countryCodeTarget.value)
    }

    if (this.hasPostalCodeTarget) {
      reviewOrderTargetURL.searchParams.set("zip_code", this.postalCodeTarget.value)
    }

    if (this.hasEmailAccountsTarget) {
      reviewOrderTargetURL.searchParams.set("email_accounts", this.emailAccountsTarget.value)
    }

    if (this.searchesValue) {
      reviewOrderTargetURL.searchParams.set("searches", this.searchesValue)
    }

    if (this.verificationsValue) {
      reviewOrderTargetURL.searchParams.set("verifications", this.verificationsValue)
    }

    this.reviewOrderTarget.src = reviewOrderTargetURL.toString()
    this.reviewOrderTarget.reload()

    this._registerReviewOrderLoadedListener()
  }

  updateOverageQuotaPrices(event) {
    if (event.target.checked) {
      this.overageQuotaPricesTarget.classList.remove("d-none")
    } else {
      this.overageQuotaPricesTarget.classList.add("d-none")
    }
  }

  // Unfortunately if a Stimulus action is defined on the country select
  // "change" event, it isn't triggered when the value of the select is updated
  // programmatically (when the address is selected from the Google Maps results),
  // for example. Therefore, that event listener must be registered manually.
  _registerCountryCodeListener() {
    var _this = this

    if (this.hasCountryCodeTarget) {
      this.countryCodeTarget.addEventListener("change", function () {
        _this.updateReviewOrder()
      })
    }
  }

  _registerReviewOrderLoadedListener() {
    if (this.hasReviewOrderTarget) {
      this.reviewOrderTarget.loaded.then(() => {
        App.popover("#checkout_review_order [data-bs-toggle='popover']")

        // Only disables the Pay button is something has changed compared to the current subscription.
        // Else, leaves it disabled.
        this.enablePayButtonIfNeeded()
      })
    }
  }

  _hasToSelectBucketToTransfer() {
    return this.hasSearchBucketTarget && this.hasVerifyBucketTarget
  }

  enablePayButtonIfNeeded() {
    let baseCondition = this.baseLevelValue == 5 || this.levelTarget.value != this.baseLevelValue || this.billingCycleTarget.value != this.baseBillingCycleValue || (this.hasEmailAccountsTarget && this.emailAccountsTarget.value != this.baseEmailAccountsValue)

    if (this._hasToSelectBucketToTransfer()) {
      let hasSelectedBucketToTransfer = this.searchBucketTarget.checked || this.verifyBucketTarget.checked

      if (baseCondition && hasSelectedBucketToTransfer) {
        this.payButtonTarget.removeAttribute("disabled")
      }
    } else if (baseCondition) {
      this.payButtonTarget.removeAttribute("disabled")
    }
  }

  hideNewCardForm() {
    if (this.hasNewCardTarget) {
      $(this.newCardTarget).hide()
    }

    if (this.hasCurrentCardTarget) {
      $(this.currentCardTarget).show()
    }

    this.checkButtonState()
  }
}
